// ============================================================================
// CheckoutRegisterReportScreen — Checkout Register (BR-RPT-003..006)
// ============================================================================
// One row per booking checked out in the period (by the actual checkout): GRC,
// guest, party, who settles (Charge to), and the booking's money — Booked,
// Advance (received before arrival), Collected (from arrival on, less refunds),
// Balance — whether or not night audit has posted the stay or it is invoiced.
// The invoice state and the Check column say what is still to do, so Front
// Office and Accounts can reconcile checkouts before invoicing.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import DateRangePresetSelector from 'components/common/DateRangePresetSelector';
import { useDateRangePreset } from 'hooks/useDateRangePreset';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import { useBookingSources } from 'hooks/api/useBookingSources';
import { SETTLEMENT_MODE_OPTIONS, settlementModeLabel } from 'api/types/settlement.types';
import type { CheckoutInvoiceState, CheckoutRegisterReport, CheckoutRegisterRequest, CheckoutRegisterRow } from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatShort = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-';
const formatTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

const INVOICE_STATE: Record<CheckoutInvoiceState, { label: string; badge: string }> = {
  NOT_INVOICED: { label: 'Not invoiced', badge: 'badge-secondary' },
  PART_INVOICED: { label: 'Part invoiced', badge: 'badge-warning' },
  INVOICED: { label: 'Invoiced', badge: 'badge-success' },
};
const INVOICE_STATE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Any status' },
  ...(Object.keys(INVOICE_STATE) as CheckoutInvoiceState[]).map((k) => ({ value: k, label: INVOICE_STATE[k].label })),
];
const CHARGE_TO_OPTIONS: SelectOption[] = [
  { value: '', label: 'Anyone' },
  ...SETTLEMENT_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];
/** Flags the guest or Accounts must act on are red; billing still to do is amber. */
const FLAG_BADGE: Record<string, string> = {
  'Guest balance': 'badge-danger',
  'Excess received': 'badge-danger',
  'Invoiced differs': 'badge-warning',
  'Part invoiced': 'badge-warning',
  'Not invoiced': 'badge-secondary',
};

const Money: React.FC<{ v?: number | null; strong?: boolean; tone?: string }> = ({ v, strong, tone }) => (
  <span style={{ display: 'block', textAlign: 'right', fontWeight: strong ? 600 : undefined, color: tone }}>
    {v == null ? '—' : formatCurrency(v)}
  </span>
);

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

type Row = CheckoutRegisterRow & { id: string };

const CheckoutRegisterReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const dateRange = useDateRangePreset('TODAY');
  const { from, to } = dateRange.value;
  const [bookingSource, setBookingSource] = useState('');
  const [chargeTo, setChargeTo] = useState('');
  const [invoiceState, setInvoiceState] = useState('');
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const { options: enumSourceOptions } = useBookingSources();
  const sourceOptions: SelectOption[] = useMemo(() => [{ value: '', label: 'Any source' }, ...enumSourceOptions], [enumSourceOptions]);

  const enabled = !!propertyId && !!from && !!to && from <= to;
  const request: CheckoutRegisterRequest = useMemo(() => ({
    propertyId, from, to,
    bookingSource: bookingSource || undefined,
    chargeTo: chargeTo || undefined,
    invoiceState: (invoiceState || undefined) as CheckoutInvoiceState | undefined,
    exceptionsOnly,
  }), [propertyId, from, to, bookingSource, chargeTo, invoiceState, exceptionsOnly]);

  const [report, setReport] = useState<CheckoutRegisterReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      setReport(await reportService.getCheckoutRegister(request));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The report did not load.');
    } finally {
      setLoading(false);
    }
  }, [enabled, request]);
  useEffect(() => { void load(); }, [load]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) { notificationService.toast.warning('Select a property and a valid period first.'); return; }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const { blob, disposition } = await reportService.exportCheckoutRegister(request, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, `CheckoutRegister_${from}_${to}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, request, from, to]);

  const rows = useMemo<Row[]>(() => (report?.rows ?? []).map((r) => ({ ...r, id: r.bookingId })), [report]);

  const columns = useMemo<SyutGridColumn<Row>[]>(() => [
    {
      id: 'checkedOutAt', header: 'Checked out', accessorKey: 'checkedOutAt', size: 115, enableSorting: true,
      cell: ({ row }) => (
        <span>{formatDate(row.original.checkoutDate)}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{formatTime(row.original.checkedOutAt)}{row.original.checkedOutByName ? ` · ${row.original.checkedOutByName}` : ''}</div>
        </span>
      ),
    },
    {
      id: 'bookingNumber', header: 'Booking / GRC', accessorKey: 'bookingNumber', size: 150, enableSorting: true,
      cell: ({ row }) => (
        <span style={{ fontFamily: 'monospace' }}>{row.original.bookingNumber}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.grcNumbers || 'no GRC'}</div>
        </span>
      ),
    },
    {
      id: 'guestName', header: 'Guest / Rooms', accessorKey: 'guestName', size: 180, enableSorting: true,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {row.original.roomNumbers ? `Room ${row.original.roomNumbers}` : `${row.original.rooms} room(s)`}
            {` · ${formatShort(row.original.arrivalDate)} – ${formatShort(row.original.checkoutDate)}`}
          </div>
        </span>
      ),
    },
    {
      id: 'partyName', header: 'Source / Party', accessorKey: 'partyName', size: 170, enableSorting: true,
      cell: ({ row }) => (
        <span>{row.original.partyName || row.original.bookingSource}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {row.original.partyName ? row.original.bookingSource : ''}
            {row.original.otaBookingRef ? `${row.original.partyName ? ' · ' : ''}ref ${row.original.otaBookingRef}` : ''}
          </div>
        </span>
      ),
    },
    {
      id: 'settlementMode', header: 'Charge to', accessorKey: 'settlementMode', size: 150, enableSorting: true,
      cell: ({ row }) => row.original.settlementMode === 'GUEST'
        ? <span>Guest</span>
        : (
          <span>{row.original.partyName || 'Party'}
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.settlementMode === 'CREDIT_FULL' ? 'full stay' : 'booking only'}</div>
          </span>
        ),
    },
    { id: 'booked', header: 'Booked', accessorKey: 'booked', size: 110, enableSorting: true, cell: ({ row }) => <Money v={row.original.booked} /> },
    { id: 'advance', header: 'Advance', accessorKey: 'advance', size: 105, enableSorting: true, cell: ({ row }) => <Money v={row.original.advance} /> },
    { id: 'collected', header: 'Collected', accessorKey: 'collected', size: 105, enableSorting: true, cell: ({ row }) => <Money v={row.original.collected} /> },
    {
      id: 'balance', header: 'Balance', accessorKey: 'balance', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const b = row.original.balance;
        const tone = Math.abs(b) < 1 ? 'var(--text-muted)'
          : b < 0 || row.original.settlementMode === 'GUEST' ? 'var(--danger, #dc2626)' : undefined;
        return <Money v={b} strong tone={tone} />;
      },
    },
    {
      id: 'invoiceState', header: 'Invoice', accessorKey: 'invoiceState', size: 170, enableSorting: true,
      cell: ({ row }) => {
        const s = INVOICE_STATE[row.original.invoiceState] ?? INVOICE_STATE.NOT_INVOICED;
        return (
          <span>
            <span className={`badge ${s.badge}`} style={{ fontSize: '0.62rem' }}>{s.label}</span>
            {row.original.invoiceNumbers && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace', whiteSpace: 'normal' }}>
                {row.original.invoiceNumbers}{row.original.creditNoteCount > 0 ? ` +${row.original.creditNoteCount} CN` : ''}
              </div>
            )}
          </span>
        );
      },
    },
    { id: 'invoiced', header: 'Invoiced', accessorKey: 'invoiced', size: 110, enableSorting: true, cell: ({ row }) => <Money v={row.original.invoiced} /> },
    {
      id: 'flags', header: 'Check', accessorKey: 'flags', size: 170,
      cell: ({ row }) => row.original.flags.length === 0
        ? <span style={{ color: 'var(--success, #16a34a)', fontSize: '0.8rem' }}>✓</span>
        : (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
            {row.original.flags.map((f) => <span key={f} className={`badge ${FLAG_BADGE[f] ?? 'badge-secondary'}`} style={{ fontSize: '0.62rem' }}>{f}</span>)}
          </span>
        ),
    },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={16} />, label: exporting ? 'Exporting…' : 'Export', tooltip: 'Export to Excel', onClick: () => { void exportFile('xlsx'); }, variant: 'icon' });
      buttons.push({ id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Print', tooltip: 'Print the register', onClick: () => { void exportFile('pdf'); }, variant: 'icon' });
    }
    return buttons;
  }, [load, allowedActions, exporting, printing, exportFile]);

  const appliedFilterCount = [dateRange.value.preset !== 'TODAY', !!bookingSource, !!chargeTo, !!invoiceState, exceptionsOnly].filter(Boolean).length;
  const clearFilters = () => {
    dateRange.setPreset('TODAY');
    setBookingSource('');
    setChargeTo('');
    setInvoiceState('');
    setExceptionsOnly(false);
    // Property is intentionally NOT reset — every report needs a property scope.
  };

  const periodLabel = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Checked out', value: periodLabel, primary: true, title: 'By the actual checkout date (when the last room left)' },
    ...(report ? [
      { label: 'Checkouts', value: String(report.rows.length) },
      { label: 'Booked', value: formatCurrency(report.booked), title: "The bookings' totals, whether or not posted or invoiced" },
      { label: 'Advance', value: formatCurrency(report.advance), title: 'Received before arrival' },
      { label: 'Collected', value: formatCurrency(report.collected), title: 'Received from arrival on, less refunds' },
      { label: 'Guest due', value: formatCurrency(report.guestDue), title: 'Balance of the stays the guest settles' },
      { label: 'Party receivable', value: formatCurrency(report.partyReceivable), title: 'Balance of the stays an agent, corporate or OTA settles' },
      { label: 'Invoiced', value: formatCurrency(report.invoiced), title: 'Invoices less credit notes, of the stays invoiced so far' },
      { label: 'Exceptions', value: String(report.exceptions), title: 'Stays with something in the Check column' },
    ] : []),
  ];

  const footer = report && report.rows.length > 0 ? (
    <>
      <b>Charge to</b>
      {report.byChargeTo.map((t) => <span key={t.key}>{settlementModeLabel(t.key)} {t.count} · balance <b>{formatCurrency(t.balance)}</b></span>)}
      <b style={{ marginLeft: '0.75rem' }}>Invoice</b>
      {report.byInvoiceState.map((t) => <span key={t.key}>{INVOICE_STATE[t.key as CheckoutInvoiceState]?.label ?? t.key} <b>{t.count}</b></span>)}
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Checkout Register"
      description="Bookings checked out in the period — booked, advance, collected and balance, who settles, and how far invoicing has got."
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: appliedFilterCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Checkout date</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <DateRangePresetSelector
                    preset={dateRange.value.preset}
                    from={from}
                    to={to}
                    onPresetChange={dateRange.setPreset}
                    onFromChange={dateRange.setCustomFrom}
                    onToChange={dateRange.setCustomTo}
                  />
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>At most a year.</div>
              </div>
              <div className="filter-section">
                <SyutSelect label="Booking source" value={bookingSource} onChange={(v) => setBookingSource((v as string) || '')}
                  options={sourceOptions} isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Charge to" value={chargeTo} onChange={(v) => setChargeTo((v as string) || '')}
                  options={CHARGE_TO_OPTIONS} isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Invoice status" value={invoiceState} onChange={(v) => setInvoiceState((v as string) || '')}
                  options={INVOICE_STATE_OPTIONS} isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={exceptionsOnly} onChange={(e) => setExceptionsOnly(e.target.checked)} />
                  Exceptions only
                </label>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  A guest balance or excess received, not or part invoiced, or invoiced differently from booked.
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property and a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error}</div>
        ) : (
          <SyutGrid
            data={rows}
            columns={columns}
            isLoading={loading}
            enableSorting
            frozenLeftColumns={['checkedOutAt', 'bookingNumber']}
            emptyMessage={exceptionsOnly ? 'No exceptions among the checkouts in the period.' : 'No checkouts in the period.'}
          />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default CheckoutRegisterReportScreen;
